import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid, Link, Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { FeedKey, PublicUserProfileFragment } from 'api';
import { getFullName } from 'helpers';
import { generateLink } from 'Links';
import { FollowToggle, IFollowToggleRenderProps, UserAvatar } from 'components';

interface IProps {
  user: PublicUserProfileFragment;
}

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(2),
  },
  avatar: {
    marginBottom: 12,
  },
  icon: {
    color: theme.palette.grey[500],
  },
}));

const UserCard: FC<IProps> = ({ user }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  function renderFollowButton(followToggleProps: IFollowToggleRenderProps) {
    const { isFollowing, loading, onClick } = followToggleProps;

    const i18nKey = isFollowing ? 'common:following' : 'common:follow';

    return (
      <Button
        fullWidth
        onClick={onClick}
        color={isFollowing ? 'default' : 'primary'}
        variant="outlined"
        disabled={loading}
      >
        {t(i18nKey)}
      </Button>
    );
  }

  function renderProfileButton() {
    return (
      <Button component={generateLink(`/profile/${user.id}`)} fullWidth variant="outlined">
        {t('components:viewProfile')}
      </Button>
    );
  }

  function renderFollowToggle() {
    return (
      <FollowToggle source={user} type={FeedKey.User}>
        {followToggleProps =>
          followToggleProps.disableToggle
            ? renderProfileButton()
            : renderFollowButton(followToggleProps)
        }
      </FollowToggle>
    );
  }

  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <Paper className={classes.paper}>
        <Grid container direction="column" alignItems="center" wrap="nowrap">
          <Grid item xs>
            <UserAvatar link className={classes.avatar} user={user} size={100} />
          </Grid>
          <Grid item xs>
            <Box textOverflow="ellipsis" whiteSpace="noWrap" overflow="hidden">
              <Link
                title={getFullName(user)}
                component={generateLink(`/profile/${user.id}`)}
                variant="subtitle2"
              >
                {getFullName(user)}
              </Link>
            </Box>
          </Grid>
          <Grid item xs>
            <Box textOverflow="ellipsis" whiteSpace="noWrap" overflow="hidden">
              <Typography title={user.job_title} paragraph noWrap>
                {user.job_title}
              </Typography>
            </Box>
          </Grid>
          {renderFollowToggle()}
        </Grid>
      </Paper>
    </Grid>
  );
};

export default UserCard;
